import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import useParticipantes from "../hooks/useParticipantes";
import { isHojeEvento } from "../config/evento";
import {
  CORES,
  combinaBusca,
  ehPresente,
  formatarTelefone,
  statusConfirmacao,
  subtituloConvidado,
} from "../utils/participante";
import {
  AvatarIniciais,
  BotaoAcao,
  CampoBusca,
  CardResumo,
  Etiqueta,
  Pagina,
  celula as celulaBase,
  celulaCabecalho,
} from "./ui/Painel";

const NOME_DISTRITAL_PUBLICA = "Distrital Inscricao Publica";

const FILTROS = [
  { id: "todos", label: "Todos" },
  { id: "confirmados", label: "Confirmados" },
  { id: "presentes", label: "Presentes" },
  { id: "pendentes", label: "Pendentes" },
];

const porcentagem = (parte, total) => (total ? Math.round((parte / total) * 100) : 0);

const celula = { ...celulaBase, whiteSpace: "nowrap" };

const ParticipantesEvento = () => {
  const navigate = useNavigate();
  const { role, participantes, distritaisMap, loading, erro, atualizadoEm, registrarEntrada, salvarMesa, excluir } =
    useParticipantes();
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState("todos");
  const [excluindo, setExcluindo] = useState(null);
  const [exportandoPdf, setExportandoPdf] = useState(false);
  const [registrando, setRegistrando] = useState(null);
  const [vagas, setVagas] = useState(null);

  useEffect(() => {
    const consultar = () =>
      axios
        .get(`${process.env.REACT_APP_API_URL}/participantes/vagas`)
        .then(({ data }) => setVagas(data))
        .catch(() => {});
    consultar();
    const intervalo = setInterval(consultar, 30000);
    return () => clearInterval(intervalo);
  }, []);

  const ehAdmin = role === "admin";
  const podeRegistrarEntrada = (role === "admin" || role === "usuario") && isHojeEvento();

  const nomeDistrital = (p) => {
    const nome = distritaisMap[p.distrital_id] || "";
    return nome === NOME_DISTRITAL_PUBLICA ? "Inscrição pública" : nome;
  };

  const resumo = useMemo(() => {
    const total = participantes.length;
    const confirmados = participantes.filter((p) => statusConfirmacao(p) === "confirmado").length;
    const presentes = participantes.filter(ehPresente).length;
    const pendentes = participantes.filter((p) => statusConfirmacao(p) === "pendente").length;
    return { total, confirmados, presentes, pendentes };
  }, [participantes]);

  const contagemFiltro = {
    todos: resumo.total,
    confirmados: resumo.confirmados,
    presentes: resumo.presentes,
    pendentes: resumo.pendentes,
  };

  const visiveis = participantes
    .filter((p) => {
      if (filtro === "confirmados") return statusConfirmacao(p) === "confirmado";
      if (filtro === "presentes") return ehPresente(p);
      if (filtro === "pendentes") return statusConfirmacao(p) === "pendente";
      return true;
    })
    .filter((p) => combinaBusca(p, distritaisMap[p.distrital_id] || "", busca))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

  const handleRegistrarEntrada = async (id) => {
    setRegistrando(id);
    try {
      await registrarEntrada(id);
    } catch (error) {
      console.error("Erro ao registrar entrada:", error);
      alert(error.response?.data?.error || "Erro ao registrar entrada");
    } finally {
      setRegistrando(null);
    }
  };

  const handleSalvarMesa = async (p, valor) => {
    if (valor === (p.mesa || "")) return;
    try {
      await salvarMesa(p.id, valor);
    } catch (error) {
      console.error("Erro ao salvar mesa:", error);
      alert("Erro ao salvar mesa");
    }
  };

  const handleExcluir = async () => {
    const alvo = excluindo;
    setExcluindo(null);
    try {
      await excluir(alvo.id);
    } catch (error) {
      console.error("Erro ao excluir participante:", error);
      alert("Erro ao excluir participante");
    }
  };

  const exportarCSV = () => {
    const header = [
      "Nome",
      "E-mail",
      "Telefone",
      "Distrital",
      "Município",
      "Mesa",
      "Confirmação",
      "Compareceu",
    ];
    const rows = visiveis.map((p) => [
      p.nome,
      p.email || "",
      formatarTelefone(p.telefone),
      nomeDistrital(p),
      p.municipio || "",
      p.mesa || "",
      statusConfirmacao(p) === "confirmado" ? "Confirmado" : "Pendente",
      ehPresente(p) ? "Sim" : "Não",
    ]);

    const escapar = (valor) => `"${String(valor ?? "").replace(/"/g, '""')}"`;
    const csv = "﻿" + [header, ...rows].map((linha) => linha.map(escapar).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "participantes.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportarPdf = async () => {
    setExportandoPdf(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/participantes/exportar-pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "lista-convidados.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao exportar PDF:", error);
      alert("Erro ao exportar PDF");
    } finally {
      setExportandoPdf(false);
    }
  };

  return (
    <Pagina
      eyebrow="Gestão do evento"
      titulo="Lista de convidados"
      descricao={
        vagas &&
        (vagas.esgotado
          ? `Inscrições encerradas: limite de ${vagas.limite.toLocaleString("pt-BR")} vagas atingido.`
          : `Inscrições: ${vagas.inscritos.toLocaleString("pt-BR")} de ${vagas.limite.toLocaleString("pt-BR")} vagas (${vagas.restantes.toLocaleString("pt-BR")} restantes).`)
      }
      acoes={
        <>
          <BotaoAcao icon={<FileDownloadOutlinedIcon />} onClick={exportarCSV}>
            CSV
          </BotaoAcao>
          <BotaoAcao icon={<FileDownloadOutlinedIcon />} onClick={exportarPdf} disabled={exportandoPdf}>
            {exportandoPdf ? "Gerando..." : "PDF"}
          </BotaoAcao>
          {(role === "admin" || role === "usuario") && (
            <BotaoAcao primario icon={<QrCodeScannerIcon />} onClick={() => navigate("/leitorqrcode")}>
              Abrir leitor QR
            </BotaoAcao>
          )}
        </>
      }
    >
      <Dialog open={!!excluindo} onClose={() => setExcluindo(null)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Excluir convidado</DialogTitle>
        <DialogContent>
          Tem certeza que deseja excluir <strong>{excluindo?.nome}</strong>?
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button onClick={() => setExcluindo(null)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleExcluir} variant="contained" color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <CardResumo titulo="Convidados" valor={resumo.total} detalhe="na lista" progresso={100} cor={CORES.navy} />
          <CardResumo
            titulo="Confirmados"
            valor={resumo.confirmados}
            detalhe={`${porcentagem(resumo.confirmados, resumo.total)}%`}
            progresso={porcentagem(resumo.confirmados, resumo.total)}
            cor={CORES.azul}
          />
          <CardResumo
            titulo="Presentes"
            valor={resumo.presentes}
            detalhe={`${porcentagem(resumo.presentes, resumo.confirmados)}% dos confirmados`}
            progresso={porcentagem(resumo.presentes, resumo.confirmados)}
            cor={CORES.verde}
          />
          <CardResumo
            titulo="Aguardando resposta"
            valor={resumo.pendentes}
            detalhe={`${porcentagem(resumo.pendentes, resumo.total)}%`}
            progresso={porcentagem(resumo.pendentes, resumo.total)}
            cor={CORES.ambar}
          />
        </Box>

        <Box sx={{ backgroundColor: "#fff", border: `1px solid ${CORES.borda}`, borderRadius: "10px", overflow: "hidden" }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.25,
              p: 2,
              borderBottom: `1px solid ${CORES.borda}`,
            }}
          >
            <CampoBusca
              value={busca}
              onChange={setBusca}
              placeholder="Buscar por nome, distrital, município ou telefone"
              sx={{ flex: "1 1 280px" }}
            />
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              {FILTROS.map((f) => {
                const ativo = filtro === f.id;
                return (
                  <ButtonBase
                    key={f.id}
                    onClick={() => setFiltro(f.id)}
                    sx={{
                      height: 30,
                      px: 1.5,
                      borderRadius: "16px",
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: "inherit",
                      gap: 0.75,
                      border: `1px solid ${ativo ? CORES.navy : CORES.borda}`,
                      backgroundColor: ativo ? CORES.navy : "#fff",
                      color: ativo ? "#fff" : CORES.texto,
                    }}
                  >
                    {f.label}
                    <Box component="span" sx={{ fontSize: 11, opacity: 0.7, fontWeight: 500 }}>
                      {contagemFiltro[f.id]}
                    </Box>
                  </ButtonBase>
                );
              })}
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={34} sx={{ color: CORES.navy }} />
            </Box>
          ) : erro && participantes.length === 0 ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {erro}
            </Alert>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...celulaCabecalho, pl: 2 }}>Convidado</TableCell>
                    <TableCell sx={celulaCabecalho}>
                      Distrital /<br />
                      Município
                    </TableCell>
                    <TableCell sx={celulaCabecalho}>Telefone</TableCell>
                    <TableCell sx={celulaCabecalho}>Associado</TableCell>
                    <TableCell sx={celulaCabecalho}>Interesse</TableCell>
                    <TableCell sx={celulaCabecalho}>Participação</TableCell>
                    <TableCell sx={celulaCabecalho}>Mesa</TableCell>
                    <TableCell sx={celulaCabecalho}>Confirmação</TableCell>
                    <TableCell sx={{ ...celulaCabecalho, textAlign: "right", pr: 2 }}>Check-in</TableCell>
                    {ehAdmin && <TableCell sx={{ ...celulaCabecalho, width: 40 }} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiveis.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={ehAdmin ? 10 : 9} sx={{ ...celula, textAlign: "center", py: 5, color: CORES.textoSuave }}>
                        Nenhum convidado encontrado.
                      </TableCell>
                    </TableRow>
                  )}
                  {visiveis.map((p) => {
                    const presente = ehPresente(p);
                    const confirmado = statusConfirmacao(p) === "confirmado";
                    const subtitulo = subtituloConvidado(p);
                    return (
                      <TableRow key={p.id} hover sx={{ "&:hover": { backgroundColor: "#fafbfc" } }}>
                        <TableCell sx={{ ...celula, pl: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                            <AvatarIniciais nome={p.nome} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: CORES.texto, lineHeight: 1.3 }}>
                                {p.nome}
                              </Typography>
                              <Typography sx={{ fontSize: 11, color: CORES.textoSuave, lineHeight: 1.3 }}>
                                {subtitulo || p.email || "—"}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={celula}>
                          <Typography sx={{ fontSize: 12.5, lineHeight: 1.3 }}>{nomeDistrital(p) || "—"}</Typography>
                          {p.municipio && (
                            <Typography sx={{ fontSize: 11, color: CORES.textoSuave, lineHeight: 1.3 }}>
                              {p.municipio}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ ...celula, fontVariantNumeric: "tabular-nums", letterSpacing: 0.3 }}>
                          {formatarTelefone(p.telefone) || "—"}
                        </TableCell>
                        <TableCell sx={celula}>
                          {p.associado_acsp === null || p.associado_acsp === undefined ? (
                            <Box component="span" sx={{ color: "#a3adb8" }}>—</Box>
                          ) : p.associado_acsp ? (
                            "Sim"
                          ) : (
                            <Box component="span" sx={{ color: "#8a97a6" }}>Não</Box>
                          )}
                        </TableCell>
                        <TableCell sx={celula}>
                          {p.interesse_associar === "sim_contato" ? (
                            <Box component="span" sx={{ color: CORES.verde, fontWeight: 500 }}>Sim</Box>
                          ) : p.interesse_associar === "nao_obrigado" ? (
                            <Box component="span" sx={{ color: "#8a97a6" }}>Não</Box>
                          ) : (
                            <Box component="span" sx={{ color: "#a3adb8" }}>—</Box>
                          )}
                        </TableCell>
                        <TableCell sx={celula}>
                          {p.forma_participacao ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Box
                                sx={{
                                  width: 5,
                                  height: 5,
                                  borderRadius: "50%",
                                  backgroundColor: p.forma_participacao === "presencial" ? CORES.navy : "#9aa6b2",
                                }}
                              />
                              {p.forma_participacao === "presencial" ? "Presencial" : "Online"}
                            </Box>
                          ) : (
                            <Box component="span" sx={{ color: "#a3adb8" }}>—</Box>
                          )}
                        </TableCell>
                        <TableCell sx={celula}>
                          <InputBase
                            key={`${p.id}-${p.mesa || ""}`}
                            defaultValue={p.mesa || ""}
                            placeholder="—"
                            onBlur={(e) => handleSalvarMesa(p, e.target.value.trim())}
                            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                            inputProps={{ "aria-label": `Mesa de ${p.nome}`, maxLength: 50 }}
                            sx={{
                              width: 44,
                              height: 28,
                              fontSize: 12.5,
                              border: `1px solid ${CORES.borda}`,
                              borderRadius: "5px",
                              "& input": { textAlign: "center", p: 0 },
                              "&.Mui-focused": { borderColor: CORES.navyClaro },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={celula}>
                          {confirmado ? <Etiqueta tom="verde">Confirmado</Etiqueta> : <Etiqueta tom="ambar">Pendente</Etiqueta>}
                        </TableCell>
                        <TableCell sx={{ ...celula, textAlign: "right", pr: 2 }}>
                          {presente ? (
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                px: 1,
                                py: 0.5,
                                fontSize: 11.5,
                                fontWeight: 600,
                                color: CORES.verde,
                                backgroundColor: CORES.verdeFundo,
                                border: "1px solid #b9e0c8",
                                borderRadius: "5px",
                              }}
                            >
                              ✓ Presente
                            </Box>
                          ) : (
                            <Tooltip title={podeRegistrarEntrada ? "" : "Disponível no dia do evento"}>
                              <span>
                                <Button
                                  size="small"
                                  disabled={!podeRegistrarEntrada || registrando === p.id}
                                  onClick={() => handleRegistrarEntrada(p.id)}
                                  sx={{
                                    height: 28,
                                    px: 1.25,
                                    py: 0,
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    color: CORES.navyClaro,
                                    border: `1px solid ${CORES.borda}`,
                                    borderRadius: "5px",
                                    whiteSpace: "nowrap",
                                    transition: "none",
                                    "&:hover": { backgroundColor: "#f3f6f9", transform: "none" },
                                  }}
                                >
                                  {registrando === p.id ? "Registrando..." : "Registrar entrada"}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                        {ehAdmin && (
                          <TableCell sx={{ ...celula, pl: 0 }}>
                            <Tooltip title="Excluir">
                              <IconButton size="small" onClick={() => setExcluindo(p)} sx={{ color: "#9aa6b2" }}>
                                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${CORES.borda}`,
              fontSize: 11.5,
              color: CORES.textoSuave,
            }}
          >
            <span>
              Mostrando {visiveis.length} de {resumo.total} convidados
            </span>
            <span>
              {erro
                ? "Sem conexão — tentando novamente"
                : atualizadoEm
                  ? Date.now() - atualizadoEm.getTime() < 10000
                    ? "Atualizado agora"
                    : `Atualizado às ${atualizadoEm.toLocaleTimeString("pt-BR")}`
                  : ""}
            </span>
          </Box>
        </Box>
    </Pagina>
  );
};

export default ParticipantesEvento;
